/** Max images per review/reply — keep in sync with the backend limit. */
export const MAX_REVIEW_IMAGES = 5;

export type ReviewImageVisibility = 'public' | 'private';

export interface ReviewImage {
  url: string;
  visibility: ReviewImageVisibility;
}

/** Defensive parse of an untyped GraphQL payload. */
export function parseReviewImages(value: unknown): ReviewImage[] {
  if (!Array.isArray(value)) return [];
  const out: ReviewImage[] = [];
  for (const item of value) {
    if (!item || typeof item !== 'object') continue;
    const { url, visibility } = item as Record<string, unknown>;
    if (typeof url !== 'string') continue;
    if (visibility !== 'public' && visibility !== 'private') continue;
    out.push({ url, visibility });
  }
  return out;
}

export function filterPublicReviewImages(value: unknown): ReviewImage[] {
  return parseReviewImages(value).filter((i) => i.visibility === 'public');
}

/** Appends freshly uploaded urls as private images, capped at MAX_REVIEW_IMAGES. */
export function appendPrivateReviewImages(current: ReviewImage[], urls: string[]): ReviewImage[] {
  const added: ReviewImage[] = urls.map((url) => ({ url, visibility: 'private' }));
  return [...current, ...added].slice(0, MAX_REVIEW_IMAGES);
}

export function toggleReviewImageVisibility(current: ReviewImage[], index: number): ReviewImage[] {
  return current.map((image, i) =>
    i === index
      ? { ...image, visibility: image.visibility === 'public' ? 'private' : 'public' }
      : image,
  );
}

export function removeReviewImage(current: ReviewImage[], index: number): ReviewImage[] {
  return current.filter((_, i) => i !== index);
}
